// Content edited from /admin, read at request time.
//
// The site is statically generated, so anything imported from data/content.ts
// is fixed at build time. This fetches the live rows instead, which is what
// makes an edit made on a phone appear without a redeploy.
//
// Fetched once per page load and shared, so three components asking for the
// same collection cost one request. Falls back to whatever was compiled in, so
// an unreachable database or an unconfigured site shows the built-in content
// rather than an empty page.

const state = {
  promise: null,
  data: null,
};

// What the site does when it has not been told otherwise: the resume is
// reachable and the library is open. A database that is unreachable therefore
// leaves the site as it was rather than locking things by accident.
const DEFAULT_SETTINGS = {
  resumeUrl: null,
  resumeEnabled: true,
  libraryLocked: false,
  avatarUrl: null,
};

const EMPTY = { prompts: [], sheets: [], posts: [], generations: [], counts: {}, settings: DEFAULT_SETTINGS };

export function useLiveContent() {
  function fetchAll() {
    if (state.data) return Promise.resolve(state.data);
    if (state.promise) return state.promise;

    // Kicked off by an inline script in <head>, so by the time anything here
    // runs the request is usually already in flight or already answered. Taken
    // once and then dropped: it resolves to null when it failed, and reusing a
    // settled failure would mean the retry below never actually retries.
    let early = null;
    if (typeof window !== 'undefined' && window.__content) {
      early = window.__content;
      window.__content = null;
    }

    state.promise = (early || fetch('/.netlify/functions/content').then((r) => (r.ok ? r.json() : null)))
      .then((data) => {
        if (!data) throw new Error('content unavailable');
        state.data = data;
        return state.data;
      })
      .catch(() => {
        // Deliberately NOT written to state.data. Caching the empty answer was
        // a bug: EMPTY is a truthy object, so the `if (state.data)` above
        // returned it for the rest of the page's life and nothing ever asked
        // again. One aborted request — a slow dev server, a dropped connection,
        // a reload landing mid-flight — therefore emptied the whole site until
        // the next full page load, with the panels that had already resolved
        // keeping their content and the ones that had not showing nothing.
        //
        // Left uncached, this call still answers EMPTY so the caller renders
        // its fallback, and the next caller retries.
        return EMPTY;
      })
      .finally(() => {
        state.promise = null;
      });

    return state.promise;
  }

  // Shared, so the hero, the contact panel and the library gate all read one
  // answer rather than each asking. Starts at the defaults and updates when
  // the fetch lands, which keeps the prerendered markup usable in between.
  function useSettings() {
    const settings = useState('liveSettings', () => ({ ...DEFAULT_SETTINGS }));

    if (typeof window !== 'undefined') {
      fetchAll().then((data) => {
        settings.value = { ...DEFAULT_SETTINGS, ...(data?.settings ?? {}) };
      });
    }

    return settings;
  }

  // View and download counts, keyed by kind and then by item id. Shared like
  // the settings, and empty until the fetch lands so the prerendered markup
  // never shows a number it would have to take back.
  //
  // Only what the server decided to publish arrives here: a count below its
  // floor is absent rather than present and hidden, so there is nothing to read
  // out of the payload that the page is not already showing.
  function useCounts() {
    const counts = useState('liveCounts', () => ({}));

    if (typeof window !== 'undefined') {
      fetchAll().then((data) => {
        counts.value = data?.counts ?? {};
      });
    }

    return counts;
  }

  // Whether the one request is still in flight, so a page can show a skeleton
  // in the place the live rows will land instead of a gap that fills in
  // silently. Shared, like the data itself, so several panels asking cost one
  // request and agree about when it finished.
  //
  // Starts true, including while prerendering, which is correct: the built
  // HTML genuinely does not have this content in it, and the client fetches it
  // on mount. Server and client therefore render the same first frame, so
  // there is no hydration mismatch.
  function usePending() {
    const pending = useState('liveContentPending', () => true);

    if (typeof window !== 'undefined') {
      fetchAll().finally(() => {
        pending.value = false;
      });
    }

    return pending;
  }

  // Live rows lead, then whatever is compiled in that the database has not
  // replaced. When there is enough written to stand on its own, empty the
  // arrays in data/content.ts and the site becomes purely server-driven with no
  // change here.
  //
  // Live first, deliberately. The built-in entries are stubs and seeds, and
  // they were sitting above real work written from the admin, which is the
  // wrong way round: a placeholder should be what you find at the bottom after
  // the real thing, not what greets you.
  async function load(collection, fallback = []) {
    if (typeof window === 'undefined') return fallback;

    const data = await fetchAll();
    const rows = data?.[collection];
    if (!Array.isArray(rows) || !rows.length) return fallback;

    // Ids cannot collide (built-in ones are hand-written slugs, live ones are
    // uuids) but this guards the list keys anyway.
    const seen = new Set(rows.map((row) => row.id));
    return [...rows, ...fallback.filter((item) => !seen.has(item.id))];
  }

  return { load, useSettings, useCounts, usePending };
}
